import React from 'react';
import { useIntl } from 'react-intl';
import { MdArrowForward, MdComputer } from 'react-icons/md';
import type { AppManifest } from '@api/apps';
import { cardFormatsOf, cardLabel, runsOnLabel, type CardFormats } from './appCardText';
import { displayFormats } from './appFormats';
import { presentationOf } from './appPresentation';
import { localized } from './appRunView';
import { useAppCover } from './useAppCover';
import {
  Card,
  CardBody,
  CardCover,
  CardIcon,
  CoverCaption,
  CoverTagline,
  CoverTitle,
  CardMeta,
  CardTagline,
  CardTitle,
  FormatChip,
  FormatGroup,
  RunsOnBadge,
} from './AppCard.styled';

const Formats: React.FC<{ extensions: readonly string[] }> = ({ extensions }) => (
  <FormatGroup>
    {displayFormats(extensions).map((format) => (
      <FormatChip key={format}>{format}</FormatChip>
    ))}
  </FormatGroup>
);

const FormatsLine: React.FC<{ appKey: string; formats: CardFormats }> = ({ appKey, formats }) => {
  if (formats.takes.length === 0 && formats.delivers.length === 0) return null;
  return (
    <CardMeta data-testid={`apps-gallery__formats-${appKey}`}>
      {formats.takes.length > 0 && <Formats extensions={formats.takes} />}
      {formats.delivers.length > 0 && (
        <>
          <MdArrowForward size={14} aria-hidden="true" />
          <Formats extensions={formats.delivers} />
        </>
      )}
    </CardMeta>
  );
};

interface AppCardProps {
  readonly appKey: string;
  readonly manifest: AppManifest;
  readonly onOpen?: () => void;
}

const AppCard: React.FC<AppCardProps> = ({ appKey, manifest, onOpen }) => {
  const intl = useIntl();
  const { icon: Icon } = presentationOf(manifest);
  const { cover: shownCover, onCoverError } = useAppCover(manifest);
  const name = localized(intl, manifest.name);
  const tagline = localized(intl, manifest.tagline);
  const formats = cardFormatsOf(manifest);
  const runsOn = runsOnLabel(intl, manifest.runsOn);
  const runsOnBadge = (overlay: boolean) =>
    runsOn && (
      <RunsOnBadge $overlay={overlay} aria-hidden="true" data-testid={`apps-gallery__runs-on-${appKey}`}>
        <MdComputer size={12} />
        {runsOn}
      </RunsOnBadge>
    );

  return (
    <Card
      type="button"
      onClick={onOpen}
      aria-label={cardLabel(intl, { name, tagline, formats, runsOn })}
      data-testid={`apps-gallery__card-${appKey}`}
      $picture={Boolean(shownCover)}
    >
      {shownCover ? (
        <>
          <CardCover
            src={shownCover}
            alt=""
            aria-hidden="true"
            onError={onCoverError}
            data-testid={`apps-gallery__cover-${appKey}`}
          />
          {/* Only built-in art has its title drawn in; a user app keeps its coverPreset beside a generated thumbnail. */}
          {manifest.presentation?.coverPreset && (
            <CoverCaption aria-hidden="true">
              <CoverTitle>{name}</CoverTitle>
              <CoverTagline>{tagline}</CoverTagline>
            </CoverCaption>
          )}
          {runsOnBadge(true)}
        </>
      ) : (
        <CardBody>
          <CardIcon aria-hidden="true">
            <Icon size={24} />
          </CardIcon>
          <CardTitle>{name}</CardTitle>
          <CardTagline>{tagline}</CardTagline>
          {runsOnBadge(false)}
          <FormatsLine appKey={appKey} formats={formats} />
        </CardBody>
      )}
    </Card>
  );
};

export default AppCard;
